import Chevron from "@/common/icons/chevron"
import { Return } from "@/components/common/icons/return"
import { Info } from "@/components/common/icons/info"
import cx from "classnames"
import { motion } from "framer-motion"
import { useEffect, useMemo, useRef, useState } from "react"
import { A11y, Navigation, Pagination, Scrollbar } from "swiper/modules"
import "swiper/css"
import { Swiper, SwiperSlide } from "swiper/react"
import { Counter } from "./counter"
import { foodData, slidesData } from "./data.js"
import { swiper } from "./swiper.module.css"

export const calculate = (people, food) => {
  const data = slidesData.map((slide) => {
    const total = food.reduce((acc, label) => {
      const foodItem = foodData.find((f) => f.label === label)
      const value =
        people === 225
          ? foodItem.data.find((d) => d.label === slide.label).valueSchool
          : foodItem.data.find((d) => d.label === slide.label).value * people

      return acc + value
    }, 0)

    return { ...slide, total }
  })

  return data
}

function Result({ people, food }) {
  const prevRef = useRef(null)
  const nextRef = useRef(null)
  const [active, setActive] = useState([])
  const [mounted, setMounted] = useState(false)

  useEffect(() => setMounted(true), [])

  const result = useMemo(() => {
    setActive([])
    return calculate(people, food)
  }, [people, food])

  if (!mounted) return null

  return (
    <Swiper
      className={swiper}
      modules={[Pagination, Navigation, Scrollbar, A11y]}
      pagination={{
        clickable: true
      }}
      spaceBetween={24}
      slidesPerView={1.15}
      scrollbar={{
        el: ".swiper-scrollbar",
        draggable: true
      }}
      breakpoints={{
        640: {
          slidesPerView: 1.5,
          spaceBetween: 24
        },
        768: {
          slidesPerView: 2.1,
          spaceBetween: 32
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 32,
          pagination: false
        },
        1280: {
          slidesPerView: 3,
          spaceBetween: 48,
          pagination: false
        }
      }}
      navigation={{
        nextEl: ".swiper-nav-next",
        prevEl: ".swiper-nav-prev"
      }}
      onBeforeInit={(swiper) => {
        swiper.params.navigation.prevEl = prevRef.current
        swiper.params.navigation.nextEl = nextRef.current
      }}>
      {result.map((slide, index) => (
        <SwiperSlide key={index}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              duration: 0.5,
              delay: 1 + index * 0.25
            }}
            className="relative h-80 lg:h-96 perspective">
            <div
              className={cx("relative preserve-3d w-full h-full duration-500", {
                "my-rotate-y-180": active.includes(index)
              })}>
              <div className="absolute z-20 w-full h-full overflow-hidden my-rotate-y-180 backface-hidden">
                <div className="w-full h-full p-4 text-base bg-white rounded-lg lg:p-8 xl:text-lg">
                  {slide.info}
                  <button
                    type="button"
                    aria-label="Stäng"
                    onClick={() => setActive(active.filter((v) => v !== index))}
                    className="absolute flex items-center justify-center w-10 h-10 transition-colors duration-200 rounded-full z-80 top-4 right-4 bg-summer-green-25 hover:bg-summer-green-75">
                    <Return className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="absolute z-10 w-full h-full backface-hidden">
                <div className="flex flex-col items-center justify-end w-full h-full p-8 overflow-hidden bg-white rounded-lg">
                  <button
                    type="button"
                    aria-label="Öppna"
                    onClick={() => setActive([...active, index])}
                    className="absolute flex items-center justify-center w-10 h-10 transition-colors duration-200 rounded-full z-70 top-4 right-4 bg-summer-green-25 hover:bg-summer-green-75">
                    <Info className="w-6 h-6" />
                  </button>
                  <div className="flex-auto w-full h-full">
                    <figure className="relative z-10 w-full h-full max-h-49">
                      <div
                        className={`absolute z-10 inset-0 flex justify-center items-center transition-all duration-300 ${
                          slide.total < 500
                            ? "opacity-100 visible"
                            : "opacity-0 invisible"
                        }`}>
                        {slide.illustration.small}
                      </div>
                      <div
                        className={`absolute z-10 inset-0 flex justify-center items-center transition-all duration-300 ${
                          slide.total > 500 && slide.total < 1000
                            ? "opacity-100 visible"
                            : "opacity-0 invisible"
                        }`}>
                        {slide.illustration.medium}
                      </div>
                      <div
                        className={`absolute z-10 inset-0 flex justify-center items-center transition-all duration-300 ${
                          slide.total > 1000
                            ? "opacity-100 visible"
                            : "opacity-0 invisible"
                        }`}>
                        {slide.illustration.large}
                      </div>
                    </figure>
                  </div>

                  <span className="font-serif font-bold leading-none whitespace-nowrap">
                    <Counter slide={slide} value={slide.total} />
                    <span className="text-2xl">
                      {" "}
                      {slide.unitAlternative &&
                      slide.unitLimit &&
                      slide.total > slide.unitLimit
                        ? slide.unitAlternative
                        : slide.unit}
                    </span>
                  </span>
                  <h5 className="text-black-50">{slide.title}/år</h5>
                </div>
              </div>
            </div>
          </motion.div>
        </SwiperSlide>
      ))}
      <div className="no-print lg:hidden">
        <button
          type="button"
          aria-label="Föregående"
          className="swiper-nav-prev swiper-nav"
          ref={prevRef}>
          <Chevron className="w-full h-auto transform rotate-90 fill-current" />
        </button>
        <button
          type="button"
          aria-label="Nästa"
          className="swiper-nav-next swiper-nav"
          ref={nextRef}>
          <Chevron className="w-full h-auto transform -rotate-90 fill-current" />
        </button>
      </div>
    </Swiper>
  )
}

export default Result
